import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  listPendingBusinessRequests, reviewBusinessRequest,
  listVerifiedBusinesses, revokeBusinessVerification,
} from '../../services/adminService';
import '../Dashboard/BusinessDashboard.css';
import './AdminPanel.css';

const SERVICE_LABELS = { tours: 'Guided Tours', lessons: 'Coaching & Lessons' };

function BusinessFields({ r }) {
  return (
    <div className="admin-card__fields">
      <div><span className="admin-card__k">Contact</span><span className="admin-card__v">{r.name}</span></div>
      <div><span className="admin-card__k">Website</span><span className="admin-card__v">
        {r.businessWebsite ? <a href={r.businessWebsite} target="_blank" rel="noopener noreferrer">{r.businessWebsite}</a> : '—'}
      </span></div>
      <div><span className="admin-card__k">Services</span><span className="admin-card__v">
        {Array.isArray(r.businessServices) && r.businessServices.length
          ? r.businessServices.map(s => SERVICE_LABELS[s] || s).join(', ')
          : '—'}
      </span></div>
      <div><span className="admin-card__k">Socials</span><span className="admin-card__v">
        {Array.isArray(r.businessLinks) && r.businessLinks.length
          ? r.businessLinks.map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>{l.type}: {l.url}</a>
            ))
          : '—'}
      </span></div>
      <div><span className="admin-card__k">ABN</span><span className="admin-card__v">{r.businessABN || '—'}</span></div>
      <div>
        <span className="admin-card__k">Insurance doc</span>
        <span className="admin-card__v">
          {r.businessInsuranceUrl
            ? <a href={r.businessInsuranceUrl} target="_blank" rel="noopener noreferrer">{r.businessInsuranceFileName || 'View document'}</a>
            : '—'}
        </span>
      </div>
    </div>
  );
}

// Same full-screen shell as BusinessDashboard (topbar + left nav + content) —
// reuses its CSS classes so admin and business surfaces look consistent.
export function AdminPanel({ onClose }) {
  const [tab, setTab] = useState('pending'); // 'pending' | 'verified'
  const [pending,  setPending]  = useState(null); // null = loading
  const [verified, setVerified] = useState(null);
  const [error,   setError]   = useState('');
  const [busyUid, setBusyUid] = useState(null);

  useEffect(() => { loadPending(); loadVerified(); }, []);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function loadPending() {
    try {
      setPending(await listPendingBusinessRequests());
    } catch (err) {
      setError(err.message);
    }
  }

  async function loadVerified() {
    try {
      setVerified(await listVerifiedBusinesses());
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleReview(uid, approve) {
    setBusyUid(uid);
    setError('');
    try {
      await reviewBusinessRequest(uid, approve);
      setPending(prev => prev.filter(r => r.uid !== uid));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyUid(null);
    }
  }

  async function handleRevoke(uid) {
    if (!window.confirm('Revoke this business’s verification? They’ll lose the ability to create new tours/lessons or connect Stripe — anything already published stays live.')) return;
    setBusyUid(uid);
    setError('');
    try {
      await revokeBusinessVerification(uid);
      setVerified(prev => prev.filter(r => r.uid !== uid));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyUid(null);
    }
  }

  const requests = tab === 'pending' ? pending : verified;

  return createPortal(
    <div className="bd-overlay">
      <div className="bd-shell">
        <div className="bd-topbar">
          <div className="bd-topbar__title">
            <span className="bd-topbar__name">Admin — Verification</span>
          </div>
          <button className="bd-topbar__close" onClick={onClose}>← Back to map</button>
        </div>

        <div className="bd-body">
          <nav className="bd-nav">
            <button
              className={`bd-nav__item${tab === 'pending' ? ' bd-nav__item--active' : ''}`}
              onClick={() => setTab('pending')}
            >
              Pending{pending?.length > 0 && <span className="bd-section-count">{pending.length}</span>}
            </button>
            <button
              className={`bd-nav__item${tab === 'verified' ? ' bd-nav__item--active' : ''}`}
              onClick={() => setTab('verified')}
            >
              Verified{verified?.length > 0 && <span className="bd-section-count">{verified.length}</span>}
            </button>
          </nav>

          <div className="bd-content">
            <div className="bd-tab">
              {error && <div className="admin-panel__error">{error}</div>}
              {requests === null && !error && <p className="admin-panel__muted">Loading…</p>}
              {requests?.length === 0 && (
                <p className="admin-panel__muted">
                  {tab === 'pending' ? 'No pending verification requests.' : 'No verified businesses yet.'}
                </p>
              )}

              {tab === 'pending' && requests?.map(r => (
                <div className="admin-card" key={r.uid}>
                  <div className="admin-card__name">
                    {r.businessName || r.name} <span className="admin-card__email">{r.email}</span>
                  </div>
                  <BusinessFields r={r} />
                  <div className="admin-card__actions">
                    <button
                      className="admin-btn admin-btn--reject"
                      disabled={busyUid === r.uid}
                      onClick={() => handleReview(r.uid, false)}
                    >
                      Reject
                    </button>
                    <button
                      className="admin-btn admin-btn--approve"
                      disabled={busyUid === r.uid}
                      onClick={() => handleReview(r.uid, true)}
                    >
                      {busyUid === r.uid ? 'Working…' : 'Approve'}
                    </button>
                  </div>
                </div>
              ))}

              {tab === 'verified' && requests?.map(r => (
                <div className="admin-card" key={r.uid}>
                  <div className="admin-card__name">
                    {r.businessName || r.name} <span className="admin-card__email">{r.email}</span>
                  </div>
                  <BusinessFields r={r} />
                  <div className="admin-card__actions">
                    <button
                      className="admin-btn admin-btn--reject"
                      disabled={busyUid === r.uid}
                      onClick={() => handleRevoke(r.uid)}
                    >
                      {busyUid === r.uid ? 'Working…' : 'Revoke'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
